/**
 * Источники эмбедов у клиента.
 *
 * Отдельно от сцены, и это не организационное решение.
 *
 * В СЦЕНЕ АДРЕСА НЕТ ВОВСЕ. Слой хранит только `sourceId`; адрес
 * приходит отдельным сообщением и только тем, кому положен —
 * оверлею и клиенту с правом `embed:manage` (docs/05). Положи мы
 * его в props слоя, он уехал бы в снапшоте всем, кто эту сцену
 * видит, включая модератора, и вся возня с шифрованием потеряла
 * бы смысл.
 *
 * Отсюда же следует, что источники ПЕРЕЖИВАЮТ сцену: снапшот
 * приходит заново при каждой пересинхронизации, а список источников
 * — один раз при подключении. Сбрасывать его вместе со сценой
 * значило бы гасить все эмбеды на каждом разрыве связи.
 */
/** Источник эмбеда в том виде, в каком его прислал сервер. */
import { decodeMessage } from './message.js';
export const NO_EMBEDS = new Map();
/**
 * Разбирает сообщение `embeds`.
 *
 * Возвращает null для всего остального: вызывающий по этому
 * понимает, что сообщение не про эмбеды, и передаёт его дальше.
 *
 * Разбор придирчив к форме, но не к содержимому. Источник без
 * идентификатора отбрасывается — по нему всё равно нечего искать;
 * источник без адреса остаётся, потому что это законный случай.
 */
export function parseEmbeds(raw) {
    const parsed = decodeMessage(raw);
    if (parsed === null)
        return null;
    if (!isRecord(parsed) || parsed.t !== 'embeds')
        return null;
    if (!Array.isArray(parsed.embeds)) {
        // Сообщение про эмбеды, но без списка. Пустая карта, а не null:
        // «эмбедов нет» — это ответ, и клиент должен перестать их ждать
        return NO_EMBEDS;
    }
    const out = new Map();
    for (const item of parsed.embeds) {
        const source = toSource(item);
        if (source !== null)
            out.set(source.id, source);
    }
    return out;
}
function toSource(raw) {
    if (!isRecord(raw))
        return null;
    const id = str(raw.id);
    if (id === '')
        return null;
    return {
        id,
        name: str(raw.name),
        urlMask: str(raw.urlMask),
        urlHost: str(raw.urlHost),
        url: str(raw.url),
        defaultW: num(raw.defaultW),
        defaultH: num(raw.defaultH),
        previewSafe: raw.previewSafe === true,
        needsAudio: raw.needsAudio === true,
        minRole: str(raw.minRole),
    };
}
/**
 * Подставляет адрес источника в свойства слоя.
 *
 * Здесь, у клиента, а не на сервере — потому что сервер и так
 * прислал источники отдельно, и класть адрес в каждый слой значило
 * бы рассылать его повторно с каждой дельтой.
 *
 * Неизвестный источник даёт пустой адрес, а не ошибку: он мог быть
 * удалён, пока слой стоял на сцене, — тогда слой покажет заглушку,
 * и человек поймёт, что источника больше нет. Ошибка слоя тут
 * выглядела бы поломкой оверлея.
 *
 * Правило предпросмотра живёт ЗДЕСЬ, в общем пакете, а не в панели:
 * иначе оно оказалось бы в одном месте из двух, и первый же
 * рендерер, забывший про него, тихо запустил бы чужой чат
 * у каждого модератора.
 */
export function withEmbedURL(props, sources, context = {}) {
    if (!isRecord(props))
        return props;
    const sourceId = str(props.sourceId);
    if (sourceId === '')
        return props;
    const source = sources.get(sourceId);
    const allowed = source !== undefined && (!context.preview || source.previewSafe);
    return {
        ...props,
        url: allowed ? source.url : '',
        // Имя берётся из источника, а не из слоя: переименовали
        // источник — подпись на заглушке обязана измениться вместе
        // с ним, а не остаться от старой сцены
        name: source?.name ?? str(props.name),
    };
}
function str(value) {
    return typeof value === 'string' ? value : '';
}
function num(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=embeds.js.map